import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserService } from "../../../core/user/user.service";
import {
    NavBarCategory,
    NavBarCategoryMetadata,
    NavBarRoute,
    NavBarRouteMetadata
} from "../../../core/models/users.models";
import { navBarCategoryMetadata, navBarRouteMetadata } from "./navigator.constants";
import { AeolusService, WebSocketConnectionStatus } from "../../../modules/care/aeolus.service";
import { User } from "../../../core/user/user.types";
import { Store } from "@ngrx/store";
import { setUser } from "../../../modules/messaging/store/messaging.actions";
import { NavigationEnd, Router, Scroll } from "@angular/router";
import { distinct, pairwise, skip, Subject, tap } from "rxjs";
import posthog from "posthog-js";
import { MessagingAPIService } from "../../../modules/messaging/messaging-api.service";
import { AeolusAPIService } from "../../../modules/messaging/aeolus-api.service";
import { notEmptyFilter } from "../../../shared/utils/rx-js.utils";

interface NavBarRouteX extends NavBarRouteMetadata {
    badge?: string
}

interface NavBarCategoryX extends NavBarCategoryMetadata {
    routes: NavBarRouteX[]
}

@Component({
    selector: 'navigator-layout',
    templateUrl: './navigator.component.html'
})
export class NavigatorComponent implements OnInit, OnDestroy {

    constructor(
        private _userService: UserService,
        private _aeolusAPIService: AeolusAPIService,
        private _messagingAPIService: MessagingAPIService,
        private _aeolusService: AeolusService,
        private store: Store<any>,
        private router: Router,
    ) {
    }


    private _unsubscribeAll: Subject<void> = new Subject<void>();

    protected connectionStatus: WebSocketConnectionStatus;
    protected user: User;
    protected showAppNeedsUpdateBanner: boolean = false;

    protected showSideBar: boolean = true;

    protected navBarRoutesByCategory: NavBarCategoryX[];


    ngOnInit(): void {
        this._aeolusService.socketConnectionStatus
            .pipe(
                tap((status: WebSocketConnectionStatus) => {
                    this.connectionStatus = status;
                }),
                notEmptyFilter(),
                distinct(),
                tap((status: WebSocketConnectionStatus) => {
                    if (status === WebSocketConnectionStatus.open) {
                        this._aeolusAPIService.upsertTopicSubscription('root/incoming_call_notifications', {}).subscribe();
                        this._aeolusAPIService.upsertTopicSubscription('chat/query_count_change', {}).subscribe();
                    }
                })
            )
            .subscribe()
        this._userService.user$.subscribe(user => {
            this.user = user;
            this.store.dispatch(setUser({
                user: this.user
            }));
        });
        this._userService.appNeedsUpdate$.subscribe(needsUpdate => {
            this.showAppNeedsUpdateBanner = needsUpdate;
        })
        let availableRoutesByCategoryMap = this._userService
            .fetchAvailableRoutes()
            .reduce((map: Partial<{ [key in NavBarCategory]: NavBarRouteX[] }>, route: NavBarRoute) => {
                let navBarRouteX = navBarRouteMetadata[route];
                if (map[navBarRouteMetadata[route].category]) {
                    map[navBarRouteMetadata[route].category].push(navBarRouteX);
                } else {
                    map[navBarRouteMetadata[route].category] = [navBarRouteX];
                }
                return map
            }, {});
        let availableRoutesByCategoryList: NavBarCategoryX[] = [];
        for (let category of Object.keys(availableRoutesByCategoryMap)) {
            availableRoutesByCategoryMap[category].sort((a: NavBarRouteX, b: NavBarRouteX) => {
                return a.order - b.order;
            });
            availableRoutesByCategoryList.push({
                ...navBarCategoryMetadata[category],
                routes: availableRoutesByCategoryMap[category]
            })
        }
        availableRoutesByCategoryList.sort((a: NavBarCategoryX, b: NavBarCategoryX) => {
            return a.order - b.order;
        })
        this.navBarRoutesByCategory = availableRoutesByCategoryList;
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.showSideBar = !event.url.includes('/v3/') && !event.url.includes('/profile_picture/');
            } else if (event instanceof Scroll) {
                this.showSideBar = !event.routerEvent.url.includes('/v3/') && !event.routerEvent.url.includes('/profile_picture/');
            }
        });
        this._messagingAPIService.getOpenQueryCount(this.user.id).subscribe((count: number) => {
            if (count) {
                this._userService.queryCount = count;
            }
        });
        /* TODO(create subscription & test) */
        this._userService
            .queryCount$
            .pipe(
                tap((currentOpenQueryCount) => {
                    if (currentOpenQueryCount != undefined) {
                        if (currentOpenQueryCount > 9) {
                            this._updateBadgeValue(NavBarCategory.care, NavBarRoute.care_chat, '9+');
                            this._updateBadgeValue(NavBarCategory.sales, NavBarRoute.sales_chat, '9+');
                        } else {
                            this._updateBadgeValue(NavBarCategory.care, NavBarRoute.care_chat, currentOpenQueryCount.toString());
                            this._updateBadgeValue(NavBarCategory.sales, NavBarRoute.sales_chat, currentOpenQueryCount.toString());
                        }
                    }
                }),
                pairwise(),
                skip(1)
            )
            .subscribe(async ([prevOpenQueryCount, currentOpenQueryCount]) => {
                if (currentOpenQueryCount > prevOpenQueryCount && !['918f2b59-9b0b-4a3f-9c6d-393aa45ccf71', 'd2601e8a-4726-444e-82ea-64d4a90615f8', '2f125b02-6378-4b9a-8b57-dd5bfd4e5283'].includes(this.user.id)) {
                    await this._playNotification();
                }
            });

    }

    ngOnDestroy(): void {
        this._aeolusAPIService.deleteTopicSubscription('root/incoming_call_notifications').subscribe();
        this._aeolusAPIService.deleteTopicSubscription('chat/query_count_change').subscribe();
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private _updateBadgeValue(category: NavBarCategory, route: NavBarRoute, badgeValue: string): void {
        let navBarElement = this.navBarRoutesByCategory.find((element: NavBarCategoryX) => {
            if (element.category === category) {
                return true;
            }
        })?.routes?.find((element: NavBarRouteX) => {
            if (element.route === route) {
                return true;
            }
        })
        if (navBarElement) {
            navBarElement.badge = badgeValue;
        }
    }

    private async _playNotification() {
        let audio = new Audio();
        audio.src = "../../../../assets/sounds/notification.mp3";
        audio.load();
        try {
            // @ts-ignore
            if (navigator.userActivation.hasBeenActive) {
                // proceed with auto-playing an animation, for example
                await audio.play();
            }
        } catch (e) {
            console.error(e);
        }
    }

    reload() {
        window.location.reload();
    }

    protected readonly WebSocketConnectionStatus = WebSocketConnectionStatus;
}
